import { useBunja } from 'bunja/react';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { launchpoolItemBunja } from '@flipster/features/src/shared/launchpool/state';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { TextButton } from '@flipster/react/design-system/text-button';
import { BorderGradientBox } from '@flipster/react/design-utils/border-gradient-box';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import {
  closedColorCssRaw,
  currencyCssRaw,
  dividerCssRaw,
  dlCssRaw,
  layoutCssRaw,
} from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';

const TradingMultipliers = () => {
  const t = useTranslations();
  const router = useRouter();
  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);

  const { isLoggedIn, isCookieLoggedIn } = useAuth();
  const openDepositModal = useDepositModal();

  const minVipTierForMultiplier = launchpool?.minVipTierForMultiplier;

  const {
    stakingCurrency,
    multipliedBy,
    tradingVolume,
    multipliers,
    status,
    multiplierTooltipOverride,
    volumeTitleOverride,
    volumeCurrencyOverride,
  } = launchpool;

  const isClosed = status === LaunchpoolStatus.Closed;

  const multiplierTooltipText =
    multiplierTooltipOverride ??
    `Increase your trading volume or become a VIP ${minVipTierForMultiplier}+ to earn multiplied rewards on your staked amount.\n\nRewards depend on your daily trading volume or daily VIP status during the staking period.`;

  const onClickInfoButton = () => {
    dialog.open({
      title: {
        label: <FormattedMessage id='Autogen_common.r251126.s_Trading_multiplier' />,
      },
      content: <div className={css({ whiteSpace: 'pre-line' })}>{multiplierTooltipText}</div>,
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<FormattedMessage id='Common.r251107.Deposit' />}
            onClick={() => {
              // kyc alert 중복으로 같이 닫히므로 비동기처리.
              setTimeout(() => {
                if (!isLoggedIn) {
                  router.push('/signin');
                  return;
                }
                openDepositModal();
                segment.track(StakeEvent.StakeTradingMultiplierDepositButtonClick);
              }, 0);
            }}
          />
        ),
        secondary: (
          <Dialog.Button hierarchy='secondary' color='neutral' label={<FormattedMessage id='Common.r250401.Ok' />} />
        ),
      },
    });
    segment.track(StakeEvent.StakeTradingMultiplierClick, { symbol: stakingCurrency, multiplied_by: multipliedBy });
  };

  return (
    <BorderGradientBox
      bgColor='colors.background.default.level-1'
      gradientColor='gradients.programs.promotion'
      css={css.raw({ flex: 1, p: 12, rounded: 8 })}
    >
      <div className={cx(css(layoutCssRaw), isClosed && 'closed')}>
        <dl className={css(dlCssRaw)}>
          <div>
            <p
              className={cx(
                hstack({
                  textStyle: 'Text/M/Strong',
                  color: 'content.default.level-5',
                }),
                isClosed && css(closedColorCssRaw),
              )}
            >
              <FormattedMessage id='Autogen_common.r251126.s_Trading_multiplier' />
              <button type='button' onClick={onClickInfoButton} className={css({ buttonShape: 'inline-icon', ml: 4 })}>
                <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
              </button>
            </p>
          </div>
          <div>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Multiplied_by' />
            </dt>
            <dd>
              <span
                className={cx(
                  css({ textStyle: 'Title/XS' }),
                  multipliedBy && css({ color: 'content.accent.promotion' }),
                  isClosed && css(closedColorCssRaw),
                )}
              >
                {multipliedBy ? `${multipliedBy}x` : '-'}
              </span>
            </dd>
          </div>
          <div>
            <dt>{volumeTitleOverride || t('Autogen_common.r251126.s_Your_trading_volume')}</dt>
            <dd>
              <Formatted.Number
                value={tradingVolume}
                suffix={<span className={css(currencyCssRaw)}>{volumeCurrencyOverride || UsdtCurrency}</span>}
                zeroString='-'
              />
            </dd>
          </div>
          <div className={css(dividerCssRaw)} />
          {!multipliers.some((tvm) => tvm.multiplierType === 'MULTIPLIER_TYPE_DEFAULT') && (
            <div>
              <dt>
                <FormattedMessage id='Autogen_common.r251126.s_Stake_only' />
              </dt>
              <dd>1x</dd>
            </div>
          )}
          {multipliers.map((tvm) => {
            const isVip = tvm.multiplierType === 'MULTIPLIER_TYPE_VIP';
            return (
              <div key={`${tvm.multiplierType}-multiplied-by-${tvm.multiplier}`}>
                <dt>
                  {isVip ? (
                    <TextButton
                      as='a'
                      size='medium'
                      color='passive'
                      href='/user/status'
                      prefetch={isCookieLoggedIn}
                      label={t('Autogen_common.r251126.s_Become_VIP_minVipTierForMultiplier', {
                        minVipTierForMultiplier: minVipTierForMultiplier,
                      })}
                      onClick={() => segment.track(StakeEvent.StakeBecomeVipClick, { symbol: stakingCurrency })}
                    />
                  ) : (
                    tvm.text
                  )}
                </dt>
                <dd>{tvm.multiplier}x</dd>
              </div>
            );
          })}
        </dl>
      </div>
    </BorderGradientBox>
  );
};

export default TradingMultipliers;
